import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Settings, Building2, Mail, Calendar, 
  User, AlertTriangle, AlertCircle, Loader2, 
  Target, Activity, BarChart3, CheckSquare 
} from 'lucide-react';
import AuthContext from '../../../../context/AuthContext';
import superAdminServices from '../../../../services/superAdminServices';

export default function AllProcessesDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processData, setProcessData] = useState(null);

  useEffect(() => {
    if (id) {
      fetchProcessDetails();
    }
  }, [id]);

  const fetchProcessDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await superAdminServices.getProcessDetails(authTokens.access, id);
      setProcessData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'draft': 'bg-gray-100 text-gray-700',
      'published': 'bg-green-100 text-green-700',
      'achieved': 'bg-blue-100 text-blue-700',
      'archived': 'bg-red-100 text-red-700',
      'Active': 'bg-green-100 text-green-700',
      'Inactive': 'bg-red-100 text-red-700',
      'Pending': 'bg-yellow-100 text-yellow-700',
      'Completed': 'bg-blue-100 text-blue-700'
    };

    const colorClass = statusColors[status] || 'bg-gray-100 text-gray-700';

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colorClass}`}>
        <Settings className="h-4 w-4 mr-1" />
        {status === 'draft' ? 'Brouillon' : 
         status === 'published' ? 'Publié' : 
         status === 'achieved' ? 'Réalisé' : 
         status === 'archived' ? 'Archivé' : status}
      </span>
    );
  };

  const getProbabilityBadge = (probability) => {
    const probabilityColors = {
      'mineur': 'bg-blue-100 text-blue-700 border-blue-200',
      'normal': 'bg-green-100 text-green-700 border-green-200',
      'majeur': 'bg-orange-100 text-orange-700 border-orange-200',
      'critique': 'bg-red-100 text-red-700 border-red-200'
    };

    const colorClass = probabilityColors[probability] || 'bg-gray-100 text-gray-700 border-gray-200';

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${colorClass}`}>
        <Target className="h-3 w-3 mr-1" />
        {probability === 'mineur' ? 'Mineur' : 
         probability === 'normal' ? 'Normal' : 
         probability === 'majeur' ? 'Majeur' : 
         probability === 'critique' ? 'Critique' : probability}
      </span>
    );
  };

  const getRiskStatusBadge = (status) => {
    const statusColors = {
      'pending': 'bg-yellow-100 text-yellow-700',
      'approved': 'bg-green-100 text-green-700',
      'rejected': 'bg-red-100 text-red-700'
    };

    const colorClass = statusColors[status] || 'bg-gray-100 text-gray-700';

    return (
      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${colorClass}`}>
        {status === 'pending' ? 'En attente' : 
         status === 'approved' ? 'Approuvé' : 
         status === 'rejected' ? 'Rejeté' : status}
      </span>
    );
  };

  const getTaskStatusBadge = (status) => {
    const statusColors = {
      'pending': 'bg-yellow-100 text-yellow-700',
      'accepted': 'bg-green-100 text-green-700',
      'rejected': 'bg-red-100 text-red-700',
      'in_progress': 'bg-blue-100 text-blue-700',
      'completed': 'bg-purple-100 text-purple-700'
    };

    const colorClass = statusColors[status] || 'bg-gray-100 text-gray-700';

    return (
      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${colorClass}`}>
        {status === 'pending' ? 'En attente' : 
         status === 'accepted' ? 'Accepté' : 
         status === 'rejected' ? 'Rejeté' : 
         status === 'in_progress' ? 'En cours' : 
         status === 'completed' ? 'Terminé' : status}
      </span>
    );
  };

  const getRiskCard = (risk) => {
    return (
      <div key={risk.id} className="p-4 rounded-lg border border-gray-200 bg-white hover:shadow-sm transition-shadow">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <AlertTriangle className="h-4 w-4 text-orange-600 mr-2" />
              <h4 className="text-sm font-medium text-gray-900">{risk.risk_name}</h4>
            </div>
            
            {risk.description && (
              <p className="text-xs text-gray-600 mb-3">{risk.description}</p>
            )}
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {getProbabilityBadge(risk.probability)}
                {getRiskStatusBadge(risk.status)}
              </div>
              
              <div className="text-xs text-gray-500">
                {risk.actions_count} action(s)
              </div>
            </div>
            
            <div className="mt-2 flex items-center text-xs text-gray-500">
              <User className="h-3 w-3 mr-1" />
              <span>Créé par: {risk.created_by?.name || risk.created_by?.email || 'Inconnu'}</span>
              <span className="mx-2">•</span>
              <Calendar className="h-3 w-3 mr-1" />
              <span>{new Date(risk.created_at).toLocaleDateString('fr-FR')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getActivityCard = (activity) => {
    return (
      <div key={activity.id} className="p-6 rounded-lg border border-gray-200 bg-white">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center">
            <div className="h-10 w-10 bg-gradient-to-r from-black to-gray-900 rounded-full flex items-center justify-center mr-3">
              <Activity className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900">{activity.activity_name}</h4>
              <div className="mt-1">
                {getStatusBadge(activity.status)}
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-500">
            {activity.risks_count} risque(s)
          </div>
        </div>
        
        {activity.description && (
          <p className="text-xs text-gray-600 mb-4">{activity.description}</p>
        )}
        
        <div className="mb-4 flex items-center text-xs text-gray-500">
          <User className="h-3 w-3 mr-1" />
          <span>Créé par: {activity.created_by?.name || activity.created_by?.email || 'Inconnu'}</span>
          <span className="mx-2">•</span>
          <Calendar className="h-3 w-3 mr-1" />
          <span>{new Date(activity.created_at).toLocaleDateString('fr-FR')}</span>
        </div>

        {/* Associated Risks */}
        {activity.risks_details && activity.risks_details.length > 0 && (
          <div>
            <div className="flex items-center mb-3">
              <AlertTriangle className="h-4 w-4 text-orange-600 mr-2" />
              <h5 className="text-xs font-semibold text-gray-900">Risques associés ({activity.risks_details.length})</h5>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {activity.risks_details.map((risk) => getRiskCard(risk))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const getTaskCard = (task) => {
    return (
      <div key={task.id} className="p-4 rounded-lg border border-gray-200 bg-white hover:shadow-sm transition-shadow">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <CheckSquare className="h-4 w-4 text-blue-600 mr-2" />
              <h4 className="text-sm font-medium text-gray-900">{task.name}</h4>
            </div>
            
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                {getTaskStatusBadge(task.status)}
              </div>
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center text-xs text-gray-500">
                <User className="h-3 w-3 mr-1" />
                <span>Assigné à: {task.assigned_to?.name || task.assigned_to?.email || 'Non assigné'}</span>
              </div>
              <div className="flex items-center text-xs text-gray-500">
                <User className="h-3 w-3 mr-1" />
                <span>Créé par: {task.created_by?.name || task.created_by?.email || 'Inconnu'}</span>
                <span className="mx-2">•</span>
                <Calendar className="h-3 w-3 mr-1" />
                <span>{new Date(task.created_at).toLocaleDateString('fr-FR')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Chargement des détails...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="ml-2 text-sm text-red-700">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!processData) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="text-center py-12">
          <p className="text-gray-500">Aucune donnée de processus trouvée</p>
        </div>
      </div>
    );
  }

  const { process, organization, created_by, last_updated_by, activities, tasks } = processData;

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/admin-panel/processes')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{process.process_name}</h1>
            <p className="text-xs text-gray-500">Détails du processus</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {getStatusBadge(process.status)}
        </div>
      </div>

      <div className="space-y-6">
        {/* Process Information Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Settings className="h-5 w-5 text-purple-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-900">Informations du processus</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Nom du processus</label>
                  <p className="text-sm text-gray-900">{process.process_name}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Élément à contrôler</label>
                  {process.element_to_controle.map((element, index) => (
                      <div
                        key={element.id}
                        className="bg-gray-50 rounded-lg p-3 border border-gray-100"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-xs font-medium text-gray-900 mb-1">
                              {element.element_name}
                            </p>
                            <div className="flex items-center text-xs text-gray-500">
                              <Calendar className="h-3 w-3 mr-1" />
                              {new Date(element.control_date).toLocaleDateString('fr-FR')}
                            </div>
                          </div>
                          <span className="inline-flex items-center justify-center w-5 h-5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            {index + 1}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Point focal</label>
                  <p className="text-sm text-gray-900">{process.focal_point || 'Non défini'}</p>
                </div>
              </div>
            </div>
            
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Statut</label>
                  <div className="mt-1">
                    {getStatusBadge(process.status)}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Date du processus</label>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-sm text-gray-900">
                      {new Date(process.process_date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Date de contrôle</label>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-sm text-gray-900">
                      {new Date(process.controleDate).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Créé le</label>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-sm text-gray-900">
                      {new Date(process.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Organization Information Card */}
        {organization && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Building2 className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Organisation</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Nom de l'organisation</label>
                    <p className="text-sm text-gray-900">{organization.name}</p>
                  </div>
                  {organization.description && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Description</label>
                      <p className="text-sm text-gray-900">{organization.description}</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Statut d'abonnement</label>
                    <div className="mt-1">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        organization.is_subscribed 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {organization.is_subscribed ? 'Abonné' : 'Non abonné'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Organisation créée le</label>
                    <p className="text-sm text-gray-900">
                      {new Date(organization.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Creator and Updater Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Created By */}
          {created_by && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Créé par</h2>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-900">
                  {created_by.full_name || `${created_by.first_name} ${created_by.last_name}`.trim() || 'Nom non renseigné'}
                </p>
                <div className="flex items-center">
                  <Mail className="h-3 w-3 text-gray-400 mr-1" />
                  <p className="text-xs text-gray-600">{created_by.email}</p>
                </div>
              </div>
            </div>
          )}

          {/* Last Updated By */}
          {last_updated_by && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Dernière modification par</h2>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-900">
                  {last_updated_by.full_name || `${last_updated_by.first_name} ${last_updated_by.last_name}`.trim() || 'Nom non renseigné'}
                </p>
                <div className="flex items-center">
                  <Mail className="h-3 w-3 text-gray-400 mr-1" />
                  <p className="text-xs text-gray-600">{last_updated_by.email}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistics Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <BarChart3 className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Statistiques</h2>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Activity Statistics */}
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-lg font-semibold text-blue-600">{activities.statistics.total_activities}</div>
              <div className="text-xs text-gray-600">Activités totales</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-lg font-semibold text-green-600">{activities.statistics.activities_by_status.Active}</div>
              <div className="text-xs text-gray-600">Activités actives</div>
            </div>
            
            {/* Task Statistics */}
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-lg font-semibold text-purple-600">{tasks.statistics.total_tasks}</div>
              <div className="text-xs text-gray-600">Tâches totales</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-lg font-semibold text-orange-600">{tasks.statistics.tasks_by_status.completed}</div>
              <div className="text-xs text-gray-600">Tâches terminées</div>
            </div>
          </div>
        </div>

        {/* Associated Activities Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Activity className="h-5 w-5 text-green-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Activités associées</h2>
            </div>
            <div className="text-xs text-gray-500">
              {activities.activities_list.length} activité(s)
            </div>
          </div>

          {activities.activities_list.length > 0 ? (
            <div className="space-y-4">
              {activities.activities_list.map((activity) => getActivityCard(activity))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Aucune activité associée à ce processus</p>
            </div>
          )}
        </div>

        {/* Process Tasks Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <CheckSquare className="h-5 w-5 text-green-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Tâches du processus</h2>
            </div>
            <div className="text-xs text-gray-500">
              {tasks.tasks_list.length} tâche(s)
            </div>
          </div>

          {tasks.tasks_list.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tasks.tasks_list.map((task) => getTaskCard(task))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Aucune tâche associée à ce processus</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
